import type { ReactNode } from "react";
import { Box } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

const DEFAULT_OPACITY = 0.1;

type GhostStateProps = Omittable<{
  /** Content rendered centered over the dimmed children (e.g. a Text element) */
  overlay: ReactNode;
  /** Opacity for the ghost content (default 0.10) */
  opacity?: number;
  children: ReactNode;
}>;

/**
 * Wraps children at low opacity with a centered overlay.
 *
 * Use for "ghost" empty states — data has loaded but there is nothing to show.
 * The dimmed children hint at what the content would look like, while the
 * overlay explains the empty state.
 *
 * Local to this frozen legacy dashboard: `@salestrak/ui` has no equivalent, and
 * the feature is slated for removal with the WIDGETS flag, so this is not worth
 * promoting to a shared particle.
 */
export function GhostState({
  overlay,
  opacity = DEFAULT_OPACITY,
  children,
}: GhostStateProps) {
  return (
    // Box is `position: static` by default, so the overlay's absolute fill
    // needs this frame declared relative explicitly.
    <Box position="relative">
      <Box style={{ opacity }}>{children}</Box>
      <Box
        pointerEvents="none" position="absolute"
        inset={0}
        align="center"
        justify="center"
      >
        {overlay}
      </Box>
    </Box>
  );
}
